import { IAppSettings } from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { getParameter } from './global-parameters.utils';

/**
 * Interface pour un attribut enrichi avec son nom technique
 */
export interface AttributeValueWithTechnicalName {
    attribute: AttributeValue;
    technicalName: string;
}

/**
 * Utilitaire pour gérer les attributs de produit
 *
 * UTILISATION :
 *
 * 1. Import du helper :
 * ```typescript
 * import { getAttributeByTechnicalName, getAttributesByTechnicalNamePrefix, AttributeValueWithTechnicalName } from '../../common/utilities/attributes-helpers';
 * ```
 *
 * 2. Récupération d'un attribut complet avec nom technique :
 * ```typescript
 * const appSettings = props.context.app as IAppSettings;
 * const wbsItemAttribute = getAttributeByTechnicalName(
 *     props.data.productSpecificationData?.result,
 *     'WBS_ITEM_NAME',
 *     appSettings
 * );
 * if (wbsItemAttribute) {
 *     console.log('Attribut:', wbsItemAttribute.attribute.Name);
 *     console.log('Nom technique:', wbsItemAttribute.technicalName);
 * }
 * ```
 *
 * 3. Récupération directe de la valeur Name d'un attribut :
 * ```typescript
 * const appSettings = props.context.app as IAppSettings;
 * const wbsItemName = getAttributeNameByTechnicalName(
 *     props.data.productSpecificationData?.result,
 *     'WBS_ITEM_NAME',
 *     appSettings
 * );
 * ```
 *
 * 4. Récupération d'attributs avec préfixe :
 * ```typescript
 * const specialNeedsAttributes = getAttributesByTechnicalNamePrefix(
 *     productSpecificationData,
 *     'WBS_SN_',
 *     appSettings
 * );
 * specialNeedsAttributes.forEach(item => {
 *     console.log(`${item.technicalName}: ${item.attribute.Name}`);
 * });
 * ```
 */

/**
 * Recherche un attribut de produit par son nom technique
 *
 * @param {AttributeValue[] | undefined} productSpecificationData - Données des spécifications du produit
 * @param {string} technicalName - Nom technique de l'attribut recherché (ex: 'WBS_ITEM_NAME')
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via props.context.app as IAppSettings)
 * @returns {AttributeValueWithTechnicalName | undefined} L'attribut avec son nom technique ou undefined
 */
export const getAttributeByTechnicalName = (
    productSpecificationData: AttributeValue[] | undefined,
    technicalName: string,
    appSettings: IAppSettings
): AttributeValueWithTechnicalName | undefined => {
    if (!productSpecificationData || productSpecificationData.length === 0 || !technicalName) {
        return undefined;
    }

    for (const attribute of productSpecificationData) {
        if (attribute.RecordId) {
            const attributeTechnicalName = getParameter(attribute.RecordId.toString(), appSettings);
            if (attributeTechnicalName === technicalName.toUpperCase()) {
                return {
                    attribute,
                    technicalName: attributeTechnicalName
                };
            }
        }
    }

    return undefined;
};

/**
 * Récupère la valeur Name d'un attribut de produit par son nom technique
 *
 * @param {AttributeValue[] | undefined} productSpecificationData - Données des spécifications du produit
 * @param {string} technicalName - Nom technique de l'attribut recherché (ex: 'WBS_ITEM_NAME')
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via props.context.app as IAppSettings)
 * @returns {string} La valeur Name de l'attribut ou une chaîne vide si non trouvé
 */
export const getAttributeNameByTechnicalName = (
    productSpecificationData: AttributeValue[] | undefined,
    technicalName: string,
    appSettings: IAppSettings
): string => {
    const attributeWithTechnicalName = getAttributeByTechnicalName(productSpecificationData, technicalName, appSettings);
    return attributeWithTechnicalName?.attribute.Name || '';
};

/**
 * Récupère tous les attributs dont le nom technique contient une chaîne de caractères donnée
 *
 * @param {AttributeValue[] | undefined} productSpecificationData - Données des spécifications du produit
 * @param {string} searchString - Chaîne de caractères à rechercher dans le nom technique (ex: 'WBS_SN_')
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via props.context.app as IAppSettings)
 * @returns {AttributeValueWithTechnicalName[]} Tableau des attributs avec leurs noms techniques
 */
export const getAttributesByTechnicalNameContains = (
    productSpecificationData: AttributeValue[] | undefined,
    searchString: string,
    appSettings: IAppSettings
): AttributeValueWithTechnicalName[] => {
    if (!productSpecificationData || productSpecificationData.length === 0 || !searchString) {
        return [];
    }

    const results: AttributeValueWithTechnicalName[] = [];

    for (const attribute of productSpecificationData) {
        if (attribute.RecordId) {
            const technicalName = getParameter(attribute.RecordId.toString(), appSettings);
            if (technicalName?.includes(searchString)) {
                results.push({
                    attribute,
                    technicalName
                });
            }
        }
    }

    return results;
};

/**
 * Récupère tous les attributs dont le nom technique commence par un préfixe donné
 *
 * @param {AttributeValue[] | undefined} productSpecificationData - Données des spécifications du produit
 * @param {string} prefix - Préfixe recherché (ex: 'WBS_SN_')
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via props.context.app as IAppSettings)
 * @returns {AttributeValueWithTechnicalName[]} Tableau des attributs avec leurs noms techniques
 */
export const getAttributesByTechnicalNamePrefix = (
    productSpecificationData: AttributeValue[] | undefined,
    prefix: string,
    appSettings: IAppSettings
): AttributeValueWithTechnicalName[] => {
    if (!productSpecificationData || productSpecificationData.length === 0 || !prefix) {
        return [];
    }

    const results: AttributeValueWithTechnicalName[] = [];

    for (const attribute of productSpecificationData) {
        if (attribute.RecordId) {
            const technicalName = getParameter(attribute.RecordId.toString(), appSettings);
            if (technicalName?.startsWith(prefix)) {
                results.push({
                    attribute,
                    technicalName
                });
            }
        }
    }

    return results;
};

/**
 * Supprime la sous-chaîne 'Culinaryprep - ' (insensible à la casse) d'une chaîne fournie
 *
 * @param {string} value - La chaîne à nettoyer NB la chaîne ne provient que du nom F&O standard des recettes (product.Name)
 * @returns {string} La chaîne sans le préfixe 'Culinaryprep - '
 */
export const removeCulinaryprepPrefix = (value: string): string => {
    if (!value) {
        return value;
    }
    return value.replace(/culinaryprep\s*-\s*/i, '');
};
